import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Plus,
  Search,
  Clock,
  Users,
  Building2,
  Map,
  List,
  Edit,
  Trash2,
  Eye,
  X,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  InfoWindow,
} from "@vis.gl/react-google-maps";
import { useUser } from "@clerk/clerk-react";
import { EditLocationSheet } from "../EditLocationSheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

// Square footage range options
const SQFT_RANGES = [
  { value: "under_5000", label: "Under 5,000 sq ft", min: 0, max: 4999 },
  { value: "5000_10000", label: "5,000 - 10,000 sq ft", min: 5000, max: 10000 },
  { value: "10000_25000", label: "10,000 - 25,000 sq ft", min: 10001, max: 25000 },
  { value: "25000_50000", label: "25,000 - 50,000 sq ft", min: 25001, max: 50000 },
  { value: "50000_plus", label: "50,000+ sq ft", min: 50001, max: Infinity },
];

// Frequency options (matching the add/edit sheets)
const FREQUENCY_OPTIONS = [
  { value: "1_per_week", label: "1 time per week" },
  { value: "2_per_week", label: "2 times per week" },
  { value: "3_per_week", label: "3 times per week" },
  { value: "4_per_week", label: "4 times per week" },
  { value: "5_per_week", label: "5 times per week" },
  { value: "6_per_week", label: "6 times per week" },
  { value: "7_per_week", label: "7 times per week" },
  { value: "1_per_month", label: "1 time per month" },
  { value: "2_per_month", label: "2 times per month" },
];

// Helper function to format frequency values to human-readable labels
const formatFrequency = (value: string): string => {
  const freq = FREQUENCY_OPTIONS.find((f) => f.value === value);
  return freq ? freq.label : value;
};

interface ServiceLocation {
  _id: Id<"serviceLocations">;
  name: string;
  address: string;
  businessName: string;
  squareFootage: string;
  cleaningFrequency: string;
  serviceDays: string[];
  staffRequired?: number;
  coordinates?: { lat: number; lng: number };
  contactName?: string;
  contactTitle?: string;
  contactEmail?: string;
  contactPhone?: string;
}

type ViewMode = "list" | "map";

export default function ServiceLocationList() {
  const navigate = useNavigate();
  const [isEditLocationSheetOpen, setIsEditLocationSheetOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ServiceLocation | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [sqftFilter, setSqftFilter] = useState<string | null>(null);
  const [frequencyFilter, setFrequencyFilter] = useState<string | null>(null);

  // Fetch locations from Convex
  const { user } = useUser();
  const locations = useQuery(api.serviceLocations.getServiceLocations, user ? {} : "skip") ?? [];

  // Check if any filters are active
  const hasActiveFilters = sqftFilter !== null || frequencyFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setSqftFilter(null);
    setFrequencyFilter(null);
  };

  // Parse square footage to number
  const parseSqft = (sqft: string): number => {
    const num = parseInt(sqft.replace(/[^0-9]/g, ""), 10);
    return isNaN(num) ? 0 : num;
  };

  const filteredLocations = locations.filter((location: ServiceLocation) => {
    // Apply search filter
    const matchesSearch =
      searchQuery === "" ||
      location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.businessName.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply square footage filter
    let matchesSqft = true;
    if (sqftFilter !== null) {
      const range = SQFT_RANGES.find((r) => r.value === sqftFilter);
      if (range) {
        const sqftValue = parseSqft(location.squareFootage);
        matchesSqft = sqftValue >= range.min && sqftValue <= range.max;
      }
    }

    // Apply frequency filter
    const matchesFrequency =
      frequencyFilter === null || location.cleaningFrequency === frequencyFilter;

    return matchesSearch && matchesSqft && matchesFrequency;
  });

  // Calculate map center from locations with coordinates
  const { mapCenter, locationsWithCoords } = useMemo(() => {
    const withCoords = filteredLocations.filter((loc: ServiceLocation) => loc.coordinates);
    if (withCoords.length === 0) {
      return {
        mapCenter: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        locationsWithCoords: [],
      };
    }
    const center = {
      lat:
        withCoords.reduce((sum: any, loc: any) => sum + loc.coordinates!.lat, 0) /
        withCoords.length,
      lng:
        withCoords.reduce((sum: any, loc: any) => sum + loc.coordinates!.lng, 0) /
        withCoords.length,
    };
    return { mapCenter: center, locationsWithCoords: withCoords };
  }, [filteredLocations]);

  return (
    <DashboardLayout activeTab="clients">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Service Locations</h1>
            <p className="mt-1 text-gray-500">Manage all service locations across clients</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Search locations..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={sqftFilter || "all"}
                onValueChange={(value) => setSqftFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by sq ft" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Square Footage</SelectItem>
                  {SQFT_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={frequencyFilter || "all"}
                onValueChange={(value) => setFrequencyFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Frequencies</SelectItem>
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value: ViewMode) => value && setViewMode(value)}
              >
                <ToggleGroupItem value="list" aria-label="List view">
                  <List className="mr-2 h-4 w-4" />
                  List
                </ToggleGroupItem>
                <ToggleGroupItem value="map" aria-label="Map view">
                  <Map className="mr-2 h-4 w-4" />
                  Map
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {viewMode === "list" ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Square Footage</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLocations.map((location: any) => (
                  <TableRow key={location._id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{location.name}</div>
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="mr-1 h-3 w-3" />
                          {location.address}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        {location.businessName}
                      </div>
                    </TableCell>
                    <TableCell>{location.squareFootage}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          {formatFrequency(location.cleaningFrequency)}
                        </div>
                        {location.serviceDays && location.serviceDays.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {location.serviceDays.map((day: any) => (
                              <span
                                key={day}
                                className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                              >
                                {day.substring(0, 3)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-500" />
                        {location.staffRequired || 0} required
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={() => {
                            navigate(`/clients/locations/${location._id}`);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLocation(location);
                            setIsEditLocationSheetOpen(true);
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="h-[600px] w-full overflow-hidden rounded-md">
              {(() => {
                const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
                if (!apiKey) {
                  return (
                    <div className="flex h-full w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                      <div className="mb-4 rounded-full bg-amber-50 p-3">
                        <MapPin className="h-8 w-8 text-amber-600" />
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-gray-900">
                        Google Maps API Key Missing
                      </h3>
                      <p className="mx-auto mb-4 max-w-md text-gray-500">
                        The map cannot be displayed because the Google Maps API key is not
                        configured. Please check your environment variables.
                      </p>
                    </div>
                  );
                }

                if (locationsWithCoords.length === 0) {
                  return (
                    <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-100">
                      <div className="text-center text-gray-500">
                        <MapPin className="mx-auto mb-3 h-12 w-12 text-gray-400" />
                        <p className="mb-1 font-medium">No locations with map coordinates</p>
                        <p className="text-sm">
                          Add coordinates by editing locations or creating new ones
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <APIProvider apiKey={apiKey}>
                    <GoogleMap
                      defaultCenter={mapCenter}
                      defaultZoom={10}
                      gestureHandling="greedy"
                      disableDefaultUI={false}
                      mapId="DEMO_MAP_ID" // Required for AdvancedMarker
                    >
                      {locationsWithCoords.map((location: any) => {
                        const isSelected = selectedMarker === location._id;
                        return (
                          <AdvancedMarker
                            key={location._id}
                            position={{
                              lat: location.coordinates!.lat,
                              lng: location.coordinates!.lng,
                            }}
                            onClick={() => setSelectedMarker(location._id)}
                            className="custom-marker"
                          >
                            <div
                              className={`relative flex transform items-center justify-center transition-transform duration-200 ${isSelected ? "z-50 scale-125" : "z-10 scale-100 hover:z-20 hover:scale-110"}`}
                            >
                              <img
                                src="/map-icon.svg"
                                alt={location.name}
                                className="h-16 w-16 drop-shadow-md"
                              />
                            </div>
                          </AdvancedMarker>
                        );
                      })}

                      {selectedMarker &&
                        (() => {
                          const location = locationsWithCoords.find(
                            (loc: any) => loc._id === selectedMarker
                          );
                          if (!location) return null;
                          return (
                            <InfoWindow
                              position={{
                                lat: location.coordinates!.lat,
                                lng: location.coordinates!.lng,
                              }}
                              onCloseClick={() => setSelectedMarker(null)}
                            >
                              <div className="max-w-xs p-2">
                                <h3 className="mb-1 text-sm font-semibold">{location.name}</h3>
                                <p className="mb-1 text-xs text-gray-600">
                                  {location.businessName}
                                </p>
                                <p className="mb-2 text-xs text-gray-500">{location.address}</p>
                                <Button
                                  size="sm"
                                  className="w-full"
                                  onClick={() => navigate(`/clients/locations/${location._id}`)}
                                >
                                  View Details
                                </Button>
                              </div>
                            </InfoWindow>
                          );
                        })()}
                    </GoogleMap>
                  </APIProvider>
                );
              })()}
            </div>
          )}
        </Card>
      </div>
      {selectedLocation && (
        <EditLocationSheet
          isOpen={isEditLocationSheetOpen}
          onClose={() => setIsEditLocationSheetOpen(false)}
          location={selectedLocation}
        />
      )}
    </DashboardLayout>
  );
}
